import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { aboutData, whyChooseData, teamData } from '../mock';
import { Leaf, Stethoscope, Award, Shield } from 'lucide-react';

const iconMap = {
  Leaf: Leaf,
  Stethoscope: Stethoscope,
  Award: Award,
  Shield: Shield
};

export const About = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${aboutData.image})` }}
        />
        <div className="absolute inset-0 bg-farm-dark/70" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-6"
        >
          <h1 className="text-6xl md:text-7xl font-serif font-bold text-farm-cream mb-6">
            About <span className="text-farm-gold">Us</span>
          </h1>
          <p className="text-xl text-farm-cream/80 max-w-2xl mx-auto">
            A legacy of agricultural excellence since 1947
          </p>
        </motion.div>
      </section>

      {/* Story Section */}
      <section ref={containerRef} className="py-32 bg-farm-dark">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-5xl font-serif font-bold text-farm-cream mb-6">
                {aboutData.title}
              </h2>
              <p className="text-lg text-farm-cream/80 leading-relaxed mb-8">
                {aboutData.description}
              </p>
              <div className="space-y-3">
                {aboutData.highlights.map((highlight, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-2 h-2 bg-farm-gold rounded-full" />
                    <span className="text-farm-cream/80">{highlight}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src={aboutData.image}
                alt="Sahiwal Farms"
                className="rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-32 bg-gradient-to-b from-farm-dark to-farm-brown/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-serif font-bold text-farm-cream mb-6">
              Why Choose <span className="text-farm-gold">Us</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseData.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                  className="bg-farm-dark/50 backdrop-blur-sm border border-farm-green/20 rounded-2xl p-8 text-center hover:border-farm-gold/50 transition-all duration-500 shadow-xl hover:shadow-farm-gold/20"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="w-16 h-16 bg-farm-gold rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
                  >
                    <Icon size={32} className="text-farm-dark" />
                  </motion.div>
                  <h3 className="text-2xl font-serif font-bold text-farm-cream mb-4">
                    {item.title}
                  </h3>
                  <p className="text-farm-cream/70">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-32 bg-farm-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-serif font-bold text-farm-cream mb-6">
              Meet Our <span className="text-farm-gold">Team</span>
            </h2>
            <p className="text-xl text-farm-cream/70">
              Experienced professionals dedicated to excellence
            </p>
          </motion.div>

          <div className={`grid gap-8 justify-center ${teamData.length === 1
            ? 'grid-cols-1 max-w-sm mx-auto'
            : teamData.length === 2
              ? 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto'
              : teamData.length === 3
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto'
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
            }`}>
            {teamData.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group"
              >
                <div className="bg-farm-brown/20 backdrop-blur-sm border border-farm-green/20 rounded-2xl overflow-hidden hover:border-farm-gold/50 transition-all duration-500 shadow-xl hover:shadow-farm-gold/20">
                  {/* <div className="relative h-80 overflow-hidden">
                    <motion.img
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-farm-dark via-transparent to-transparent" />
                  </div> */}
                  <div className="p-6 text-center">
                    <h3 className="text-2xl font-serif font-bold text-farm-cream mb-2 group-hover:text-farm-gold transition-colors duration-300">
                      {member.name}
                    </h3>
                    <p className="text-farm-gold mb-4">{member.role}</p>
                    <p className="text-farm-cream/70 text-sm">{member.contact}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};